import React, { useMemo, memo } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,

} from 'recharts';
import { Box, FormControl, InputLabel, Select, MenuItem, Typography, Paper } from '@mui/material';
import { RevenueData } from '../../../../../backend/src/types';

export interface RevenueChartProps {
  data: RevenueData[];
  chartType?: 'line' | 'bar' | 'area';
  groupBy?: 'day' | 'week' | 'month';
  height?: number;
  showControls?: boolean;
  onChartTypeChange?: (type: 'line' | 'bar' | 'area') => void;
  onGroupByChange?: (groupBy: 'day' | 'week' | 'month') => void;
}

interface ChartDataPoint {
  date: string;
  amount: number;
  bookingCount: number;
  formattedDate: string;
  properties: Array<{
    propertyId: string;
    propertyName: string;
    amount: number;
  }>;
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};

const formatDate = (dateString: string, groupBy: string): string => {
  const date = new Date(dateString);
  
  switch (groupBy) {
    case 'day':
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    case 'week':
      return `Week of ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    case 'month':
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    default:
      return date.toLocaleDateString('en-US');
  }
};

const CustomTooltip: React.FC<any> = ({
  active,
  payload,
  label,
  groupBy,
}) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload as ChartDataPoint;
    
    return (
      <Paper sx={{ p: 2, maxWidth: 300 }}>
        <Typography variant="subtitle2" gutterBottom>
          {formatDate(label as string, groupBy)}
        </Typography>
        <Typography variant="body2" color="primary">
          Revenue: {formatCurrency(data.amount)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Bookings: {data.bookingCount}
        </Typography>
        {data.properties.length > 1 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Properties:
            </Typography>
            {data.properties.map((prop) => (
              <Typography key={prop.propertyId} variant="caption" display="block">
                {prop.propertyName}: {formatCurrency(prop.amount)}
              </Typography>
            ))}
          </Box>
        )}
      </Paper>
    );
  }

  return null;
};

export const RevenueChart: React.FC<RevenueChartProps> = memo(({
  data,
  chartType = 'line',
  groupBy = 'day',
  height = 400,
  showControls = true,
  onChartTypeChange,
  onGroupByChange,
}) => {
  const chartData = useMemo(() => {
    // Handle undefined or empty data
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }
    
    // Group data by date
    const groupedData = data.reduce((acc, item) => {
      const dateKey = item.date;
      
      if (!acc[dateKey]) {
        acc[dateKey] = {
          date: dateKey,
          amount: 0,
          bookingCount: 0,
          formattedDate: formatDate(dateKey, groupBy),
          properties: [],
        };
      }
      
      acc[dateKey].amount += item.amount;
      acc[dateKey].bookingCount += item.bookingCount || 0;
      
      // Track property-specific data
      const existingProperty = acc[dateKey].properties.find(
        (p) => p.propertyId === item.propertyId
      );
      
      if (existingProperty) {
        existingProperty.amount += item.amount;
      } else {
        acc[dateKey].properties.push({
          propertyId: item.propertyId,
          propertyName: item.propertyName,
          amount: item.amount,
        });
      }
      
      return acc;
    }, {} as Record<string, ChartDataPoint>);
    
    // Convert to array and sort by date
    return Object.values(groupedData).sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
  }, [data, groupBy]);

  const renderChart = () => {
    const commonProps = {
      data: chartData,
      margin: { top: 5, right: 30, left: 20, bottom: 5 },
    };

    const commonElements = (
      <>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis 
          dataKey="formattedDate" 
          tick={{ fontSize: 12 }}
          interval="preserveStartEnd"
        />
        <YAxis 
          tickFormatter={formatCurrency}
          tick={{ fontSize: 12 }}
        />
        <Tooltip content={<CustomTooltip groupBy={groupBy} />} />
        <Legend />
      </>
    );

    switch (chartType) {
      case 'bar':
        return (
          <BarChart {...commonProps}>
            {commonElements}
            <Bar 
              dataKey="amount" 
              fill="#1976d2" 
              name="Revenue"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        );
      
      case 'area':
        return (
          <AreaChart {...commonProps}>
            {commonElements}
            <Area
              type="monotone"
              dataKey="amount"
              stroke="#1976d2"
              fill="#1976d2"
              fillOpacity={0.3}
              name="Revenue"
            />
          </AreaChart>
        );
      
      case 'line':
      default:
        return (
          <LineChart {...commonProps}>
            {commonElements}
            <Line
              type="monotone"
              dataKey="amount"
              stroke="#1976d2"
              strokeWidth={2}
              dot={{ fill: '#1976d2', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6 }}
              name="Revenue"
            />
          </LineChart>
        );
    }
  };

  const totalRevenue = chartData && chartData.length > 0 
    ? chartData.reduce((sum, item) => sum + item.amount, 0) 
    : 0;
  const totalBookings = chartData && chartData.length > 0 
    ? chartData.reduce((sum, item) => sum + item.bookingCount, 0) 
    : 0;

  // Handle no data case
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height }}>
        <Typography variant="body2" color="text.secondary">
          No revenue data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {showControls && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Chart Type</InputLabel>
            <Select
              value={chartType}
              label="Chart Type"
              onChange={(e) => onChartTypeChange?.(e.target.value as 'line' | 'bar' | 'area')}
            >
              <MenuItem value="line">Line</MenuItem>
              <MenuItem value="bar">Bar</MenuItem>
              <MenuItem value="area">Area</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Group By</InputLabel>
            <Select
              value={groupBy}
              label="Group By"
              onChange={(e) => onGroupByChange?.(e.target.value as 'day' | 'week' | 'month')}
            >
              <MenuItem value="day">Day</MenuItem>
              <MenuItem value="week">Week</MenuItem>
              <MenuItem value="month">Month</MenuItem>
            </Select>
          </FormControl>
          
          <Box sx={{ ml: 'auto', textAlign: 'right' }}>
            <Typography variant="body2" color="text.secondary">
              Total Revenue: {formatCurrency(totalRevenue)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Bookings: {totalBookings}
            </Typography>
          </Box>
        </Box>
      )}
      
      <ResponsiveContainer width="100%" height={height}>
        {renderChart()}
      </ResponsiveContainer>
    </Box>
  );
});